import { useState, useEffect } from "react";
import { db } from "@/lib/firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { App as CapacitorApp } from '@capacitor/app';
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { Loader2, ShieldCheck, Clock, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import confetti from "canvas-confetti";

export default function PaymentStatusPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  const [status, setStatus] = useState<"waiting" | "success" | "timeout">("waiting");

  const [timeLeft, setTimeLeft] = useState(180);
  const [cycleTime, setCycleTime] = useState(30);

  // 🚨 SHIELD: Block Android hardware back button
  useEffect(() => {
    if (status === "waiting") {
      const backButtonListener = CapacitorApp.addListener('backButton', () => {
        toast({
          title: "Verification in progress",
          description: "Please do not press back while we confirm your payment.",
          variant: "destructive"
        });
      });
      return () => {
        backButtonListener.then(l => l.remove());
      };
    }
  }, [status, toast]);

  // TIMER LOGIC
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (status === "waiting") {
      if (timeLeft > 0) {
        timer = setInterval(() => {
          setTimeLeft((prev) => prev - 1);
          setCycleTime((prev) => (prev <= 1 ? 30 : prev - 1));
        }, 1000);
      } else {
        setStatus("timeout");
      }
    }
    return () => clearInterval(timer);
  }, [status, timeLeft]);

  // FIRESTORE LISTENER
  useEffect(() => {
    if (!user || status !== "waiting") return;

    const baselineExpiry = localStorage.getItem("dh_baseline_expiry") || "";
    const baselineHardware = localStorage.getItem("dh_baseline_hardware") || "";

    const userDocRef = doc(db, "users", user.uid);

    const unsubscribe = onSnapshot(userDocRef, (docSnap) => {
      const userData = docSnap.data();

      // Safely access the map correctly based on how Firestore returns it
      const currentMode = userData?.settings?.userMode;
      const currentExpiry = userData?.settings?.proModeExpiry || "";
      const currentHardware = userData?.settings?.lastHardwarePurchase || "";

      // SUCCESS CONDITION 1: Subscription was upgraded/extended
      const isSubscriptionSuccess = currentMode === "pro" && currentExpiry !== baselineExpiry;

      // SUCCESS CONDITION 2: Hardware order was placed
      const isHardwareSuccess = currentHardware !== "" && currentHardware !== baselineHardware;

      if (isSubscriptionSuccess || isHardwareSuccess) {
        localStorage.removeItem("dh_baseline_expiry");
        localStorage.removeItem("dh_baseline_hardware");

        setStatus("success");

        confetti({
          particleCount: 150,
          spread: 70,
          origin: { y: 0.6 },
          colors: ["#fbbf24", "#f59e0b", "#d97706", "#ffffff"],
        });

        setTimeout(() => {
          if (isHardwareSuccess) {
            setLocation("/rewards");
          } else {
            setLocation("/settings");
          }
        }, 3000);
      }
    });

    return () => unsubscribe();
  }, [user, status, setLocation]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <div className="min-h-screen bg-[#FFFBF2] dark:bg-[#1A1614] flex flex-col items-center justify-center p-8 text-center animate-in fade-in duration-300">

      {status === "waiting" && (
        <div className="space-y-6 w-full max-w-sm">
          <div className="relative w-28 h-28 mx-auto mb-4">
            <div className="absolute inset-0 border-4 border-amber-200 dark:border-amber-900/40 rounded-full animate-ping opacity-20" />
            <div className="absolute inset-0 border-4 border-amber-100 dark:border-amber-900/20 rounded-full" />
            <Loader2 className="w-28 h-28 text-[#8B1A1A] animate-spin" />
          </div>

          <h2 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Confirming Payment</h2>

          <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-xl border border-slate-200 dark:border-slate-700 w-full">
            <p className="text-sm font-bold text-slate-600 dark:text-slate-300 mb-6">
              Please wait while we sync with your bank. <br />
              <span className="text-[#8B1A1A] dark:text-red-400">Do not close the app or press back.</span>
            </p>

            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between bg-amber-50 dark:bg-amber-900/20 p-4 rounded-2xl border border-amber-100 dark:border-amber-800/50">
                <span className="text-xs font-black uppercase tracking-widest text-amber-700 dark:text-amber-500">Checking DB...</span>
                <span className="text-lg font-black text-amber-600">{cycleTime}s</span>
              </div>

              <div className="flex items-center justify-between px-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Timeout Threshold</span>
                <span className="text-xs font-black text-slate-500">{formatTime(timeLeft)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {status === "success" && (
        <div className="space-y-6 animate-in zoom-in duration-500 w-full max-w-sm">
          <div className="w-28 h-28 bg-emerald-500 rounded-full flex items-center justify-center mx-auto shadow-lg shadow-emerald-500/30">
            <ShieldCheck className="w-14 h-14 text-white" />
          </div>
          <h2 className="text-3xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Success!</h2>
          <div className="flex items-center justify-center gap-2 text-emerald-600 font-black text-base uppercase tracking-widest">
            <Sparkles className="w-5 h-5" /> Order Confirmed <Sparkles className="w-5 h-5" />
          </div>
        </div>
      )}

      {status === "timeout" && (
        <div className="space-y-6 w-full max-w-sm animate-in slide-in-from-bottom-4 duration-500">
          <Clock className="w-24 h-24 text-amber-500 mx-auto" />
          <h2 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Sync Delayed</h2>
          <div className="bg-amber-50 dark:bg-amber-900/20 p-5 rounded-2xl border border-amber-100 dark:border-amber-900/50 text-left">
            <p className="text-amber-800 dark:text-amber-300 text-sm font-semibold mb-2">
              We couldn't confirm your payment status within the 3-minute limit.
            </p>
            <p className="text-slate-600 dark:text-slate-400 text-xs font-medium leading-relaxed">
              If your payment was successful, your account will be upgraded automatically once the bank webhook fires. <strong className="text-slate-800 dark:text-slate-200">If money was deducted and the payment failed, it will be refunded automatically within 5-7 working days.</strong>
            </p>
          </div>
          <Button onClick={() => setLocation("/settings")} className="w-full h-14 rounded-2xl bg-[#8B1A1A] hover:bg-[#631212] text-white font-black uppercase tracking-widest">
            Return to Dashboard
          </Button>
        </div>
      )}
    </div>
  );
}